import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpStatusCode } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '@env';
import { ApiErrorResponse, isBrowser, RefreshStrategy, SsrService } from '@interticket/core';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { AuthService } from '../auth/auth.service';
import { StoreService } from '../store/store.service';

/**
 * HTTP Interceptor
 *
 * HTTP Interceptor enables you to catch HTTP requests and responses so that you can modify them.
 * It is very useful especially when you want to add extra headers to all outgoing requests
 * or catch error responses from the server.
 *
 * {@link} https://theinfogrid.com/tech/developers/angular/building-http-interceptor-angular-5/
 */
@Injectable()
export class AppHttpInterceptor implements HttpInterceptor {

  refreshStrategy: RefreshStrategy;

  constructor(
    private storeService: StoreService,
    private authService: AuthService,
    private ssrService: SsrService
  ) {
    this.refreshStrategy = new RefreshStrategy(
      () => this.authService.refresh(),
      request => this.authorizeRequest(request)
    );
  }

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    return next.handle(this.authorizeRequest(request))
      .pipe(
        catchError(err => {
          if (err.status === HttpStatusCode.Unauthorized) {
            if (this.authService.isAuthenticated) {
              return this.refreshStrategy.refresh(next, request, new ApiErrorResponse(err));

            } else if (this.storeService.guestToken?.accessToken) {
              this.refreshGuestToken();
            }
          }

          return throwError(new ApiErrorResponse(err));
        })
      );
  }

  private authorizeRequest(request: HttpRequest<any>): HttpRequest<any> {
    if (this.isSlackLog(request)) {
      return request;
    }

    const Authorization = this.authService.isAuthenticated
      ? 'Bearer ' + this.storeService.authToken.access_token
      : this.storeService.guestToken?.accessToken || '';

    return request.clone({ setHeaders: { Authorization } });
  }

  private refreshGuestToken(): void {
    if (this.storeService.appInitialized) {
      this.storeService.permissionError = true;
    } else {
      this.storeService.guestToken = { ...this.storeService.guestToken, accessToken: null };
      if (!this.storeService.isReloaded) {
        this.storeService.isReloaded = true;
        isBrowser && this.ssrService.location?.reload();
      }
    }
  }

  private isSlackLog(request: HttpRequest<any>): boolean {
    return request.url === environment.logging.slack.logWebhookUrl;
  }

}

